'use client';
// ═══════════════════════════════════════════════════════════════════════════
// L'EN-TÊTE ET LE PIED DE PAGE DES GUIDES, DANS LA LANGUE DU GUIDE
// ───────────────────────────────────────────────────────────────────────────
// 📏 LE DÉFAUT, mesuré le 02/09/2026 sur la page servie, signalé par le
// fondateur en lisant `/blog/ouderschapsverlof-belgie` : l'article était en
// néerlandais, tout ce qui l'entourait était en français. « Guides récents »,
// « Plus de guides », « Tous les guides → », « Nos conditions, annoncées
// d'avance », « Confidentialité », plus SEIZE titres de guides français dans
// le pied de page — et « Blog / Tarifs / Contact / Connexion » en haut, donc
// AVANT même que le lecteur ait défilé.
//
// Cause unique : `app/blog/layout.js` est un gabarit unique pour les 82 guides
// des deux langues, et un layout Next ne connaît pas la page qu'il enveloppe.
// D'où ce composant client, qui lit l'adresse — même mécanique que
// `NewsletterInline`, seul bloc du pied de page qui s'affichait correctement
// en néerlandais, et pour cette raison exacte.
//
// ⚠️ « CLIENT » NE VEUT PAS DIRE « INVISIBLE POUR GOOGLE » : `usePathname` est
// résolu pendant le rendu serveur, donc le HTML servi porte déjà la bonne
// langue et les bons liens. Aucun clignotement, aucune perte de référencement.
//
// 🔗 LES LIENS NÉERLANDAIS NE SONT PAS INVENTÉS : ils viennent de
// `liensPiedPage('nl')`, liste déjà vérifiée et verrouillée par
// `__tests__/pied-de-page-cp.test.js` (chaque cible doit être au sitemap).
// Sa règle s'applique ici telle quelle : ON NE LIE QUE VERS CE QUI EXISTE, ET
// DANS LA BONNE LANGUE. Le pied de page NL est donc plus COURT que le
// français — 14 guides au lieu de 16, et pas de `/landing/*`, qui n'ont pas de
// version néerlandaise. Un lien qui déçoit vaut moins que pas de lien.
import { usePathname } from 'next/navigation';
import { NB_CP } from '@/app/lib/nb-cp';
import { PRIX_FICHE, PRIX_IMPLEMENTATION } from '@/app/lib/prix-publics';
import { liensPiedPage } from '@/app/lib/liens-pied-page';
import { langueDuGuide, guidesRecentsDe } from '@/app/lib/langue-guide';

const PAR_COLONNE = 8;

const T = {
  fr: {
    nav: [{ h: '/blog', l: 'Blog' }, { h: '/pricing', l: 'Tarifs' }, { h: '/contact', l: 'Contact' }],
    connexion: 'Connexion',
    accroche: `Logiciel de paie belge pour PME et fiduciaires. Dimona, DmfA, Belcotax, ${NB_CP} CP.`,
    // ⚠️ Les conditions vivent ici, donc sur les 82 guides d'un coup. Treize
    // pages invitaient à nous contacter sans jamais nommer les frais : les
    // corriger une par une les aurait laissées se re-casser à la page suivante.
    // Un frais découvert en cours de route a coûté le dossier Solinas (28/07).
    conditions: `Nos conditions, annoncées d’avance : ${PRIX_IMPLEMENTATION} € de frais d’affiliation et d’implémentation, une seule fois, puis ${PRIX_FICHE} € HTVA par fiche de paie. Contrat de services de 3 ans.`,
    detail: 'Le détail',
    statut: 'Prestataire de services ONSS · DGIII/MAHI011',
    colProduit: 'Produit',
    colRecents: 'Guides récents',
    colPlus: 'Plus de guides',
    tous: 'Tous les guides →',
    hubGuides: '/blog',
    produit: [
      // ⚠️ '/' et non '/vitrine' — 02/09/2026. /vitrine répond 308 vers / (next.config.js),
      // et ce lien est servi sur ~80 articles : chacun faisait faire un saut de
      // redirection à Googlebot comme au lecteur, pour rien.
      { h: '/', l: 'Accueil' },
      { h: '/pricing', l: `Tarifs — ${PRIX_FICHE}€/fiche` },
      { h: '/landing/fiduciaires', l: 'Pour fiduciaires' },
      { h: '/landing/pme', l: 'Pour PME' },
      { h: '/contact', l: 'Nous contacter' },
    ],
    legal: [
      { h: '/legal/privacy', l: 'Confidentialité' },
      { h: '/legal/cgv', l: 'CGV' },
      { h: '/legal/mentions', l: 'Mentions légales' },
    ],
    copy: '© 2026 Aureus IA SRL · BCE BE 1028.230.781 · Schaerbeek, Bruxelles',
    filAccueil: 'Accueil',
    filBlog: 'Blog',
  },
  nl: {
    nav: [{ h: '/gids', l: 'Gidsen' }, { h: '/pricing?lang=nl', l: 'Tarieven' }, { h: '/contact', l: 'Contact' }],
    connexion: 'Inloggen',
    accroche: `Belgische loonsoftware voor kmo’s en boekhoudkantoren. Dimona, DmfA, Belcotax, ${NB_CP} PC’s.`,
    conditions: `Onze voorwaarden, vooraf aangekondigd: ${PRIX_IMPLEMENTATION} € aansluitings- en opstartkosten, eenmalig, daarna ${PRIX_FICHE} € excl. btw per loonbrief. Dienstenovereenkomst van 3 jaar.`,
    detail: 'Het detail',
    // ⚠️ « erkend sociaal secretariaat » serait FAUX et est réservé aux agréés.
    // Aureus est dienstverlener au sens RSZ : ça permet Dimona et DmfA, jamais
    // de percevoir les cotisations.
    statut: 'RSZ-dienstverlener · DGIII/MAHI011',
    colProduit: 'Product',
    colRecents: 'Recente gidsen',
    colPlus: 'Meer gidsen',
    tous: 'Alle gidsen →',
    hubGuides: '/gids',
    // La liste vérifiée du dépôt, amputée de ce qui fait doublon avec la
    // navigation du haut. Pas de /landing/* : ces pages n'ont pas de NL.
    produit: liensPiedPage('nl').filter((l) => !['/contact', '/gids'].includes(l.href))
      .map(({ href, libelle }) => ({ h: href, l: libelle })),
    // `?lang=nl` : ces pages PORTENT le néerlandais mais démarraient en
    // français — même défaut que /pricing, réparé de la même façon.
    legal: [
      { h: '/legal/privacy?lang=nl', l: 'Privacy' },
      { h: '/legal/cgv?lang=nl', l: 'Algemene voorwaarden' },
      { h: '/legal/mentions?lang=nl', l: 'Wettelijke vermeldingen' },
    ],
    copy: '© 2026 Aureus IA SRL · KBO BE 1028.230.781 · Schaarbeek, Brussel',
    filAccueil: 'Home',
    filBlog: 'Gidsen',
  },
};

const BASE = 'https://www.aureussocial.be';
const sLien = { display: 'block', color: '#888', textDecoration: 'none', fontSize: 13, marginBottom: 7 };
const sTitreCol = { fontSize: 11, fontWeight: 700, color: '#c6a34e', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 };

export function EnteteBlog() {
  const lang = langueDuGuide(usePathname());
  const t = T[lang];
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t.filAccueil, item: BASE },
          { '@type': 'ListItem', position: 2, name: t.filBlog, item: BASE + t.hubGuides },
        ],
      }) }} />
      <header style={{ background: '#0a0908', borderBottom: '3px solid #c6a34e', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <a href={lang === 'nl' ? '/?lang=nl' : '/'} style={{ color: '#c6a34e', textDecoration: 'none', fontWeight: 800, fontSize: 18 }}>
          Aureus Social Pro
        </a>
        <nav style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          {t.nav.map(({ h, l }) => (
            <a key={h} href={h} style={{ color: '#e8e6e0', textDecoration: 'none', fontSize: 14 }}>{l}</a>
          ))}
          <a href="/" style={{ background: '#c6a34e', color: '#0a0908', padding: '8px 18px', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
            {t.connexion}
          </a>
        </nav>
      </header>
    </>
  );
}

export function PiedDeBlog() {
  const lang = langueDuGuide(usePathname());
  const t = T[lang];
  const recents = guidesRecentsDe(lang, PAR_COLONNE * 2);
  const colonne1 = recents.slice(0, PAR_COLONNE);
  const colonne2 = recents.slice(PAR_COLONNE);

  return (
    <footer style={{ background: '#1a1a18', color: '#aaa', padding: '48px 24px 24px', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32, marginBottom: 40 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#c6a34e', marginBottom: 12 }}>Aureus Social Pro</div>
            <p style={{ fontSize: 13, lineHeight: 1.8, marginBottom: 12 }}>{t.accroche}</p>
            <p style={{ fontSize: 12, lineHeight: 1.8, color: '#8a8a84', marginBottom: 12 }}>
              {t.conditions}{' '}
              <a href={lang === 'nl' ? '/pricing?lang=nl' : '/pricing'} style={{ color: '#c6a34e', textDecoration: 'none' }}>{t.detail}</a>.
            </p>
            <div style={{ fontSize: 11, color: '#828282' }}>
              Aureus IA SRL · {lang === 'nl' ? 'KBO' : 'BCE'} BE 1028.230.781<br />{t.statut}
            </div>
          </div>
          <div>
            <div style={sTitreCol}>{t.colProduit}</div>
            {t.produit.map(({ h, l }) => <a key={h} href={h} style={sLien}>{l}</a>)}
          </div>
          <div>
            <div style={sTitreCol}>{t.colRecents}</div>
            {colonne1.map(({ slug, title }) => (
              <a key={slug} href={`/blog/${slug}`} style={sLien}>{title}</a>
            ))}
          </div>
          <div>
            <div style={sTitreCol}>{t.colPlus}</div>
            {colonne2.map(({ slug, title }) => (
              <a key={slug} href={`/blog/${slug}`} style={sLien}>{title}</a>
            ))}
            <a href={t.hubGuides} style={{ display: 'block', color: '#c6a34e', textDecoration: 'none', fontSize: 13, marginTop: 8, fontWeight: 600 }}>{t.tous}</a>
          </div>
        </div>
        <div style={{ borderTop: '1px solid #2a2a24', paddingTop: 20, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: '#828282' }}>{t.copy}</div>
          <div style={{ display: 'flex', gap: 16 }}>
            {t.legal.map(({ h, l }) => (
              <a key={h} href={h} style={{ color: '#828282', textDecoration: 'none', fontSize: 12 }}>{l}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
